import { useEffect, useId, useRef, useState } from 'react';
import { formatDisplayName } from '../../p2p/displayName.js';
import { formatChallengeLabel } from '../../p2p/challenges.js';
import { countryFlag } from '../../p2p/countries.js';
import { getPlayerProfile } from '../../p2p/playerProfiles.js';
import { challengeJoinUrl } from '../../p2p/challenges.js';
import { MAIN_CHAT_ROOM_ID } from '../../p2p/lobbyChat.js';
import { allowsRatedPlay } from '../../chess/game.js';
import { useI18n } from '../../i18n/useI18n.js';

const CHALLENGE_PRESETS = [
  { id: 'blitz-3', label: '3+0 blitz', timeControl: '3+0', variant: 'standard' },
  { id: 'rapid-15', label: '15+10 rapid', timeControl: '15+10', variant: 'standard' },
  { id: 'classical-90', label: '90+30 classical', timeControl: '90+30', variant: 'standard' },
  { id: 'untimed', label: '∞ untimed', timeControl: 'unlimited', variant: 'standard' },
  { id: 'bullet-1', label: '1+0 bullet', timeControl: '1+0', variant: 'standard' },
  { id: 'chess960', label: '3+0 Chess960', timeControl: '3+0', variant: 'chess960' }
];

function memberLabel(member) {
  if (member?.name) {
    return member.name;
  }
  if (member?.nickname) {
    return formatDisplayName(member.nickname, member.playerId);
  }
  return member?.playerId || 'Player';
}

function challengerDisplayName(challenge) {
  const profile = getPlayerProfile(challenge.challenger.playerId);
  if (profile?.nickname) {
    return formatDisplayName(profile.nickname, challenge.challenger.playerId);
  }
  if (challenge.challenger.nickname) {
    return formatDisplayName(challenge.challenger.nickname, challenge.challenger.playerId);
  }
  return challenge.challenger.playerId;
}

export default function LobbyChat({
  joined = false,
  members = [],
  rooms = [],
  activeRoomId = MAIN_CHAT_ROOM_ID,
  messages = [],
  sending = false,
  error = '',
  onSendMessage,
  onCreateRoom,
  onSelectRoom,
  onChallengePlayer,
  incomingChallenges = [],
  onAcceptChallenge,
  onDeclineChallenge,
  pendingOutgoingChallenge = null,
  onOpenPendingOutgoingBoard,
  onCancelPendingOutgoingChallenge
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [roomDraft, setRoomDraft] = useState('');
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [menuPlayerId, setMenuPlayerId] = useState('');
  const [challengeRated, setChallengeRated] = useState(true);
  const listRef = useRef(null);
  const menuId = useId();
  const bannerChallenge = incomingChallenges[0] || null;
  const outgoingChallenge = pendingOutgoingChallenge?.challenge || null;
  const outgoingTargetId = pendingOutgoingChallenge?.targetPlayerId || '';

  useEffect(() => {
    const node = listRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [messages, activeRoomId, expanded]);

  useEffect(() => {
    if (!menuPlayerId) {
      return undefined;
    }
    function onDocClick(event) {
      if (!event.target.closest?.('[data-challenge-menu]')) {
        setMenuPlayerId('');
      }
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [menuPlayerId]);

  async function handleSend(event) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || sending) {
      return;
    }
    const ok = await onSendMessage?.(text);
    if (ok) {
      setDraft('');
    }
  }

  async function handleCreateRoom(event) {
    event.preventDefault();
    const title = roomDraft.trim();
    if (!title || sending) {
      return;
    }
    const room = await onCreateRoom?.(title);
    if (room) {
      setRoomDraft('');
      setCreatingRoom(false);
    }
  }

  function openChallengeMenu(playerId) {
    setExpanded(true);
    setMenuPlayerId(playerId);
  }

  function challengeMember(playerId, preset) {
    setMenuPlayerId('');
    const rated = allowsRatedPlay(preset.timeControl) && challengeRated;
    onChallengePlayer?.(playerId, {
      timeControl: preset.timeControl,
      variant: preset.variant,
      rated
    });
  }

  const panel = (
    <section
      className={`lobby-chat${expanded ? ' lobby-chat-expanded' : ''}`}
      aria-label={t('lobby.chat')}
    >
      {bannerChallenge && (
        <div
          className="lobby-chat-challenge incoming"
          role="alertdialog"
          aria-labelledby="banner-challenge-title"
          aria-describedby="banner-challenge-copy"
        >
          <div className="lobby-chat-challenge-copy">
            <p className="eyebrow">{t('lobby.incoming')}</p>
            <h2 id="banner-challenge-title">
              <span className="country-flag" aria-hidden="true">
                {countryFlag(
                  bannerChallenge.challenger.country
                  || getPlayerProfile(bannerChallenge.challenger.playerId)?.country
                  || ''
                )}
              </span>
              {' '}
              {t('lobby.challengedYou', { name: challengerDisplayName(bannerChallenge) })}
            </h2>
            <p id="banner-challenge-copy">
              <strong>
                {formatChallengeLabel({
                  timeControl: bannerChallenge.timeControl,
                  variant: bannerChallenge.variant,
                  rated: bannerChallenge.rated
                })}
              </strong>
              {' · '}
              {bannerChallenge.rated ? t('lobby.ratedGame') : t('lobby.casual')}
            </p>
            <a
              className="challenge-accept-link"
              href={challengeJoinUrl(bannerChallenge)}
              onClick={(event) => {
                event.preventDefault();
                onAcceptChallenge?.(bannerChallenge);
              }}
            >
              {t('lobby.openAccept')}
            </a>
          </div>
          <div className="lobby-chat-challenge-actions">
            <button
              type="button"
              className="large-cta"
              onClick={() => onAcceptChallenge?.(bannerChallenge)}
            >
              {t('lobby.accept')}
            </button>
            <button
              type="button"
              className="text-button"
              onClick={() => onDeclineChallenge?.(bannerChallenge)}
            >
              {t('lobby.decline')}
            </button>
          </div>
        </div>
      )}

      {outgoingChallenge && !bannerChallenge && (
        <div className="lobby-chat-challenge outgoing" role="status" aria-live="polite">
          <div className="lobby-chat-challenge-copy">
            <p className="eyebrow">{t('lobby.sent')}</p>
            <h2 id="banner-outgoing-challenge-title">
              {t('lobby.waiting', {
                name: getPlayerProfile(outgoingTargetId)?.nickname
                  ? formatDisplayName(
                    getPlayerProfile(outgoingTargetId).nickname,
                    outgoingTargetId
                  )
                  : outgoingTargetId
              })}
            </h2>
            <p>
              {t('lobby.keepOpen', {
                label: formatChallengeLabel({
                  timeControl: outgoingChallenge.timeControl,
                  variant: outgoingChallenge.variant,
                  rated: outgoingChallenge.rated
                })
              })}
            </p>
          </div>
          <div className="lobby-chat-challenge-actions">
            <button
              type="button"
              className="large-cta"
              onClick={() => onOpenPendingOutgoingBoard?.()}
            >
              {t('lobby.openBoard')}
            </button>
            <button
              type="button"
              className="text-button"
              onClick={() => onCancelPendingOutgoingChallenge?.()}
            >
              {t('lobby.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="lobby-chat-toolbar">
        <div className="lobby-chat-rooms" role="tablist" aria-label="Chat rooms">
          {rooms.map((room) => (
            <button
              key={room.roomId}
              type="button"
              role="tab"
              aria-selected={room.roomId === activeRoomId}
              className={room.roomId === activeRoomId ? 'active' : ''}
              onClick={() => onSelectRoom?.(room.roomId)}
            >
              {room.roomId === MAIN_CHAT_ROOM_ID ? t('lobby.mainRoom') : (room.title || room.roomId)}
            </button>
          ))}
          <button
            type="button"
            className="text-button lobby-chat-create-room"
            onClick={() => setCreatingRoom((value) => !value)}
          >
            {t('lobby.newRoom')}
          </button>
        </div>
        <button
          type="button"
          className="text-button"
          onClick={() => setExpanded((value) => !value)}
          aria-pressed={expanded}
        >
          {expanded ? t('lobby.collapse') : t('lobby.expand')}
        </button>
      </div>

      {creatingRoom && (
        <form className="lobby-chat-room-form" onSubmit={handleCreateRoom}>
          <label className="sr-only" htmlFor="lobby-chat-room-name">{t('lobby.roomName')}</label>
          <input
            id="lobby-chat-room-name"
            value={roomDraft}
            onChange={(event) => setRoomDraft(event.target.value)}
            placeholder={t('lobby.roomName')}
            maxLength={32}
            disabled={!joined || sending}
          />
          <button type="submit" disabled={!joined || sending || !roomDraft.trim()}>
            {t('lobby.createRoom')}
          </button>
        </form>
      )}

      <div className="lobby-chat-members" aria-label="Online players" data-challenge-menu>
        {members.length === 0 && (
          <span className="muted">{t('lobby.emptyPlayers')}</span>
        )}
        {members.map((member) => {
          const open = menuPlayerId === member.playerId;
          return (
            <div
              key={member.playerId}
              className={`lobby-chat-member${open ? ' is-challenging' : ''}`}
            >
              <button
                type="button"
                className="lobby-chat-member-name"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                disabled={!joined || !onChallengePlayer}
                title={t('lobby.challenge')}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (menuPlayerId === member.playerId) {
                    setMenuPlayerId('');
                  } else {
                    openChallengeMenu(member.playerId);
                  }
                }}
              >
                <span className="country-flag" aria-hidden="true">{member.flag || '🏳️'}</span>
                {memberLabel(member)}
              </button>
              <button
                type="button"
                className="lobby-chat-challenge-button"
                disabled={!joined || !onChallengePlayer}
                title={t('lobby.challenge')}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (menuPlayerId === member.playerId) {
                    setMenuPlayerId('');
                  } else {
                    openChallengeMenu(member.playerId);
                  }
                }}
              >
                {t('lobby.challenge')}
              </button>
            </div>
          );
        })}
      </div>

      {menuPlayerId && (
        <div
          className="lobby-chat-challenge-picker"
          role="menu"
          id={menuId}
          data-challenge-menu
          aria-label={t('lobby.challengeType')}
        >
          <p className="eyebrow">
            {t('lobby.challengeName', {
              name: memberLabel(members.find((entry) => entry.playerId === menuPlayerId) || {
                playerId: menuPlayerId
              })
            })}
          </p>
          <div className="lobby-chat-challenge-picker-options">
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={challengeRated}
              className={`lobby-chat-rated-toggle${challengeRated ? ' is-on' : ''}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setChallengeRated((current) => !current);
              }}
            >
              {challengeRated ? t('lobby.ratedToggle') : t('lobby.unratedToggle')}
            </button>
            {CHALLENGE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                role="menuitem"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  challengeMember(menuPlayerId, preset);
                }}
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              className="text-button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setMenuPlayerId('');
              }}
            >
              {t('lobby.cancel')}
            </button>
          </div>
          <p className="lobby-chat-challenge-rated-hint muted">
            {challengeRated ? t('lobby.ratedHintOn') : t('lobby.ratedHintOff')}
          </p>
        </div>
      )}

      <div className="lobby-chat-messages" ref={listRef} aria-live="polite">
        {messages.length === 0 && (
          <p className="muted empty">{t('lobby.emptyMessages')}</p>
        )}
        {messages.map((message) => (
          <div className="lobby-chat-message" key={message.messageKey}>
            <strong>
              {message.nickname
                ? formatDisplayName(message.nickname, message.playerId)
                : message.playerId}
            </strong>
            <span>{message.text}</span>
            <small>{new Date(message.sentAt).toLocaleTimeString()}</small>
          </div>
        ))}
      </div>

      <form className="lobby-chat-compose" onSubmit={handleSend}>
        <label className="sr-only" htmlFor="lobby-chat-input">{t('lobby.message')}</label>
        <input
          id="lobby-chat-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={joined ? t('lobby.placeholder') : t('lobby.placeholderJoin')}
          maxLength={280}
          disabled={!joined || sending}
        />
        <button type="submit" disabled={!joined || sending || !draft.trim()}>
          {sending ? t('lobby.sending') : t('lobby.send')}
        </button>
      </form>
      {error && <p className="lobby-chat-error" role="status">{error}</p>}
    </section>
  );

  if (!expanded) {
    return panel;
  }

  return (
    <div className="lobby-chat-fullscreen" role="presentation">
      {panel}
    </div>
  );
}
